// Auth store — oturum state'i + login/logout/bootstrap actions.
import { create } from "zustand";
import type { AuthUserResponse, LoginResponse } from "@mahir/shared";
import { api, ApiError, setCsrfToken } from "../api/client";
import { rateLimitMessage } from "../lib/auth-rate-limit";
import { clearUserLocalState } from "../lib/clear-user-local-state";
import { clearAllClientSubmissions } from "../lib/client-submission";
import { clearKpiSelection } from "../lib/kpi-ui";
import { clearImportDraft } from "../lib/report-import-ui";
import { usePortalKpiStore } from "./portal-kpi-store";
import { usePortalStore } from "./portal-store";

type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated";

interface AuthState {
  status: AuthStatus;
  user: AuthUserResponse | null;
  error: string | null;

  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  // first-login auto-login sonrası: BE cookie+csrf set etti, sadece store'u işaretle
  setAuthenticated: (user: AuthUserResponse) => void;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  status: "idle",
  user: null,
  error: null,

  // Sayfa açılışında: /auth/me ile mevcut oturumu kontrol et + fresh csrfToken al.
  bootstrap: async () => {
    set({ status: "loading" });
    try {
      const res = await api<{ user: AuthUserResponse; csrfToken?: string }>("/auth/me");
      if (res.csrfToken) setCsrfToken(res.csrfToken);
      set({ status: "authenticated", user: res.user, error: null });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setCsrfToken(null);
        set({ status: "unauthenticated", user: null, error: null });
      } else {
        set({ status: "unauthenticated", user: null, error: "Oturum kontrol edilemedi" });
      }
    }
  },

  login: async (email, password) => {
    set({ status: "loading", error: null });
    try {
      const res = await api<LoginResponse>("/auth/login", {
        method: "POST",
        body: { email, password },
      });
      setCsrfToken(res.csrfToken);
      set({ status: "authenticated", user: res.user, error: null });
    } catch (err) {
      // 429 → backend'in Türkçe hız sınırı mesajı (lib/auth-rate-limit.ts tek kaynak)
      const msg =
        rateLimitMessage(err) ??
        (err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Giriş başarısız");
      set({ status: "unauthenticated", user: null, error: msg });
      throw err;
    }
  },

  logout: async () => {
    const userId = get().user?.id;
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {
      // server-side hatası olsa bile FE state'i temizleyelim
    }
    setCsrfToken(null);
    // Fix Turu 1 (IMPORTANT-1) — paylaşılan tarayıcıda bir sonraki kullanıcının
    // önceki müşterinin "gönderildi" teşekkür ekranını görmemesi için client
    // submission cache'ini tamamen temizle.
    clearAllClientSubmissions();
    // Geçmiş Rapor Aktarımı G6 — yarım kalmış parti taslağı kullanıcıya özel
    // (mahir.import.draft.v1.<userId>); paylaşılan tarayıcıda bir sonraki kullanıcının
    // önceki kullanıcının taslağını görmemesi için logout'ta temizlenir.
    if (userId) clearImportDraft(userId);
    if (userId) clearKpiSelection(userId);
    // güvenlik/UX taraması 2026-10-08 (S-L1): kullanıcı-bazlı tüm localStorage kalıntıları
    // (mahir.kpi.* / mahir.clients.recent.* / mahir.review.* / kpi.selection.* / mahir.import.draft*)
    clearUserLocalState(userId);
    // Final review I4 — paylaşılan tarayıcıda sonraki müşteri önceki firmanın adını/logosunu görmesin
    usePortalStore.getState().reset();
    usePortalKpiStore.getState().reset();
    set({ status: "unauthenticated", user: null, error: null });
  },

  setAuthenticated: (user) => {
    set({ status: "authenticated", user, error: null });
  },
}));
